import { useMutation, useQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'

import useAuthStore from '../../stores/useAuthStore/useAuthStore'
import {
    Customer,
    GetRecentCustomersResponse,
    GetRolesAndPermissionsResponse,
    User,
    UserType,
} from './types'
import {
    StartExportRequest,
    StartExportResponse,
} from './useActiveManagementApi/types'

import { useAxiosInstance } from './useAxiosInstance'
import { getCustomer } from './utils/getCustomer'

export const useApi = () => {
    const accessToken = useAuthStore((state) => state.accessToken)
    const axiosInstance = useAxiosInstance(accessToken)

    const userId = useAuthStore((state) => state.user?.id) ?? ''
    const setUser = useAuthStore((state) => state.setUser)
    const setCustomer = useAuthStore((state) => state.setCustomer)

    const useUserQuery = (queriedUserId: string) => {
        return useQuery({
            queryKey: ['users', queriedUserId, userId],
            queryFn: async () => {
                const response = await axiosInstance.get<User>(
                    `/users/${queriedUserId}`
                )
                return response.data
            },
            enabled: !!accessToken,
        })
    }

    const useCurrentUserQuery = () => {
        return useQuery({
            queryKey: ['users', 'me', accessToken],
            queryFn: async () => {
                const userResponse =
                    await axiosInstance.get<User>(`/api/v1/users/me`)
                const user = userResponse.data
                setUser(user)

                if (window.heap) {
                    window.heap.identify(userResponse.data.id)
                    window.heap.addUserProperties({
                        'Customer Email': userResponse.data.email,
                        'Customer Type': userResponse.data.type,
                    })
                }

                return {
                    user: userResponse.data,
                }
            },
            enabled: !!accessToken,
        })
    }

    const useCustomersQuery = (user: User | null) => {
        return useQuery({
            queryKey: ['users', userId, 'customers'],
            queryFn: async () => {
                let response = null

                try {
                    response = await axiosInstance.get<Customer[]>(
                        `/api/v1/users/${userId}/customers`
                    )
                } catch (error) {
                    const axiosError = error as AxiosError
                    if (
                        window.heap &&
                        axiosError.config?.headers['X-Trace-Id']
                    ) {
                        window.heap.addUserProperties({
                            'Get customers issue trace id': axiosError.config
                                .headers['X-Trace-Id'] as string,
                        })
                    }

                    throw error
                }

                const customer = getCustomer(user?.type, response.data)

                if (window.heap && customer) {
                    window.heap.addUserProperties({
                        'Customer Name': response.data[0].customerName,
                    })
                }

                if (user?.type === UserType.CUSTOMER) {
                    setCustomer(customer ?? null)
                }

                return response.data
            },
            enabled: !!accessToken && !!userId,
        })
    }

    const useLogoutMutation = () => {
        return useMutation({
            mutationFn: async (sessionId: string) => {
                const response = await axiosInstance.delete<null>(
                    `/api/v1/sessions/${sessionId}`
                )
                return response.data
            },
        })
    }

    const useRecentCustomers = () => {
        return useQuery({
            queryKey: ['customers', 'recents', userId],
            queryFn: async () => {
                const response =
                    await axiosInstance.get<GetRecentCustomersResponse>(
                        `/api/v1/users/${userId}/customers/recents`
                    )
                return response.data
            },
            enabled: !!accessToken && !!userId,
        })
    }

    const useGetRolesAndPermissions = () => {
        return useQuery({
            queryKey: ['roles-and-permissions', userId],
            queryFn: async () => {
                const response =
                    await axiosInstance.get<GetRolesAndPermissionsResponse>(
                        `/api/v1/users/me/roles-and-permissions`
                    )
                return response.data
            },
            enabled: !!accessToken && !!userId,
        })
    }

    const useStartExportMutation = () => {
        return useMutation<StartExportResponse, Error, StartExportRequest>({
            mutationFn: async (payload: StartExportRequest) => {
                const timezone =
                    Intl.DateTimeFormat().resolvedOptions().timeZone

                const response = await axiosInstance.post<StartExportResponse>(
                    '/api/v1/users/me/exports',
                    payload,
                    {
                        headers: {
                            'x-timezone': timezone,
                            'x-domain-origin': window.location.host,
                        },
                    }
                )
                return response.data
            },
        })
    }

    return {
        useUserQuery,
        useCurrentUserQuery,
        useLogoutMutation,
        useCustomersQuery,
        useRecentCustomers,
        useGetRolesAndPermissions,
        useStartExportMutation,
    }
}
